import React from 'react';
import { ShieldCheck, AlertTriangle, Thermometer, Package, Droplets, AlertCircle } from 'lucide-react';

/**
 * FoodSafetyPage - PUBLIC, no blur
 * Covers: Raw food risks, Pre-cut foods, Temperature safety,
 * Storage & hygiene, Immunocompromised considerations
 */

const SAFETY_SECTIONS = [
  {
    id: 'raw-food-risks',
    title: 'Raw Food Risks',
    icon: AlertCircle,
    color: 'red',
    content: [
      {
        heading: 'Why raw foods may carry higher risk',
        text: 'Raw or undercooked foods can contain bacteria, viruses, or parasites that cooking would normally kill. For people with weakened immune systems, these pathogens can cause more serious illness.'
      },
      {
        heading: 'Foods to be cautious about',
        text: 'Raw or undercooked meat, poultry, fish, and eggs. Unpasteurized dairy products and juices. Raw sprouts (alfalfa, bean, etc.). Sushi and sashimi. Raw oysters and shellfish.'
      },
      {
        heading: 'What this means for you',
        text: 'Discuss with your healthcare team whether you need to avoid certain raw foods. The level of caution needed depends on your individual immune status and treatment situation.'
      }
    ]
  },
  {
    id: 'pre-cut-foods',
    title: 'Pre-Cut Foods',
    icon: Package,
    color: 'amber',
    content: [
      {
        heading: 'Understanding the risks',
        text: 'Pre-cut fruits and vegetables have more surface area exposed to potential contamination. They\'ve also been handled more during processing, increasing contact opportunities.'
      },
      {
        heading: 'Safer alternatives',
        text: 'When possible, choose whole fruits and vegetables and cut them yourself at home. Wash all produce thoroughly under running water before cutting.'
      },
      {
        heading: 'When convenience is needed',
        text: 'If you need pre-cut options for energy reasons, keep them refrigerated and use quickly. Check "use by" dates and discard if they look or smell off.'
      }
    ]
  },
  {
    id: 'temperature-safety',
    title: 'Temperature Safety',
    icon: Thermometer,
    color: 'orange',
    content: [
      {
        heading: 'The danger zone',
        text: 'Bacteria multiply rapidly between 4°C and 60°C (40°F and 140°F). Food should spend as little time as possible in this temperature range.'
      },
      {
        heading: 'Cooking temperatures',
        text: 'Cook meat, poultry, and eggs to safe internal temperatures. Use a food thermometer — visual checks aren\'t reliable. Poultry: 74°C (165°F). Ground meats: 71°C (160°F). Whole cuts: 63°C (145°F) with rest time.'
      },
      {
        heading: 'Cooling and reheating',
        text: 'Refrigerate leftovers within 2 hours (1 hour if room is warm). Reheat food to at least 74°C (165°F). Don\'t leave food out at room temperature for extended periods.'
      }
    ]
  },
  {
    id: 'storage-hygiene',
    title: 'Storage & Hygiene',
    icon: Droplets,
    color: 'blue',
    content: [
      {
        heading: 'Hand washing',
        text: 'Wash hands thoroughly with soap and water before handling food, after handling raw meat, and after using the bathroom. This simple step prevents many foodborne illnesses.'
      },
      {
        heading: 'Refrigerator storage',
        text: 'Keep your refrigerator at 4°C (40°F) or below. Store raw meat on the bottom shelf to prevent drips. Use or freeze fresh meat within a few days of purchase.'
      },
      {
        heading: 'Preventing cross-contamination',
        text: 'Use separate cutting boards for raw meat and other foods. Wash all surfaces and utensils that contact raw meat before using for other foods.'
      }
    ]
  },
  {
    id: 'immunocompromised',
    title: 'Immunocompromised Considerations',
    icon: ShieldCheck,
    color: 'teal',
    content: [
      {
        heading: 'Why extra caution matters',
        text: 'CLL and its treatments can affect your immune system\'s ability to fight infections. This means foodborne pathogens that healthy people might easily overcome could cause more serious problems.'
      },
      {
        heading: 'Talk to your team',
        text: 'Your healthcare team can advise on what level of food safety precautions are appropriate for your situation. This may change over time depending on your treatment and immune status.'
      },
      {
        heading: 'Being practical',
        text: 'Food safety doesn\'t have to mean eliminating all enjoyment from eating. It\'s about being aware of risks and making informed choices. Many people with CLL eat a varied diet while taking reasonable precautions.'
      }
    ]
  }
];

const getColorClasses = (color) => {
  const colors = {
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-200', heading: 'text-red-900' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200', heading: 'text-amber-900' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-600', border: 'border-orange-200', heading: 'text-orange-900' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200', heading: 'text-blue-900' },
    teal: { bg: 'bg-teal-50', icon: 'text-teal-600', border: 'border-teal-200', heading: 'text-teal-900' }
  };
  return colors[color] || colors.blue;
};

const SafetySection = ({ section }) => {
  const Icon = section.icon;
  const colors = getColorClasses(section.color);
  
  return (
    <div className={`bg-white rounded-2xl border ${colors.border} overflow-hidden`}>
      <div className={`${colors.bg} p-5 border-b ${colors.border}`}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/80 rounded-lg">
            <Icon className={`h-6 w-6 ${colors.icon}`} />
          </div>
          <h2 className={`text-xl font-semibold ${colors.heading}`}>
            {section.title}
          </h2>
        </div>
      </div>
      
      <div className="p-6 space-y-5">
        {section.content.map((item, idx) => (
          <div key={idx}>
            <h3 className="font-medium text-slate-900 mb-2">{item.heading}</h3>
            <p className="text-slate-700 leading-relaxed">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FoodSafetyPage = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-8 w-8 text-primary" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
            Food Safety
          </h1>
        </div>
        <p className="text-lg leading-relaxed text-slate-700">
          Important food safety information for people with CLL
        </p>
      </div>

      {/* Introduction */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
          Why food safety matters
        </h2>
        <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
          <p>
            Food safety is important for everyone, but it can be especially relevant for 
            people living with CLL or undergoing treatment. Understanding basic food safety 
            principles can help you make informed choices about what and how you eat.
          </p>
          <p>
            The information here covers general food safety principles. Your healthcare team 
            can advise on what level of caution is appropriate for your individual situation.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Individual Guidance</h3>
            <p className="text-amber-800 leading-relaxed">
              Food safety recommendations may vary based on your immune status and treatment. 
              Discuss specific precautions with your healthcare team to understand what&apos;s 
              appropriate for your situation.
            </p>
          </div>
        </div>
      </div>

      {/* Safety Sections */}
      <div className="space-y-6">
        {SAFETY_SECTIONS.map((section) => (
          <SafetySection key={section.id} section={section} />
        ))}
      </div>

      {/* Bottom Summary */}
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
        <h3 className="text-xl font-semibold text-slate-900 mb-4">Key Takeaways</h3>
        <ul className="space-y-3 text-slate-700">
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Cook foods to safe temperatures, especially meat and eggs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Wash hands and surfaces frequently</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Refrigerate food promptly and check temperatures</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Be cautious with raw or undercooked foods</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Discuss specific precautions with your healthcare team</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default FoodSafetyPage;
